import { useEffect, useMemo, useState } from 'react';
import { NowApiResponse, ProductReview } from '@ids/types/now-api';
import { useUniversalFetchingHelpers } from '@ids/appshell/hooks';
import { Maybe } from '@ids/types/utility';

export const MIN_RATING = 4;
export const VISIBLE_PRODUCT_REVIEWS = 4;
export const MAX_PRODUCT_REVIEWS = 20;
export const MAX_PRODUCT_REVIEWS_WITH_INITIAL_REVIEWS = 6;

/**
 * FIXME: this hook is only a temporary solution
 *
 * It is used for fetching reviews and filtering them by ratings >= 4
 *
 * Eventually we should be able to request the API endpoint with
 * a queryParam for the rating range we want
 *
 *
 */
const useProductReviews = (sku: string, initialReviews?: Maybe<ProductReview[]>, inView = true) => {
    const [productReviews, setProductReviews] = useState<ProductReview[]>([]);
    const [normalizedReviews, setNormalizedReviews] = useState<ProductReview[]>([]);
    const { get } = useUniversalFetchingHelpers();

    const showLoadMoreButton = useMemo(
        () =>
            !!(
                productReviews?.length <= VISIBLE_PRODUCT_REVIEWS && normalizedReviews?.length > VISIBLE_PRODUCT_REVIEWS
            ),
        [productReviews, normalizedReviews]
    );

    useEffect(() => {
        if (!inView || normalizedReviews?.length >= MAX_PRODUCT_REVIEWS_WITH_INITIAL_REVIEWS) {
            return;
        }
        get<NowApiResponse<{ reviews: ProductReview[] }>>({
            path: `/web/v1/products/${sku}/reviews`,
            queryParams: { perPage: 40, onlyReviews: 'true' },
        }).then(data => {
            const reviews = data?.data.reviews || [];

            const hardcodedInitialReviews = initialReviews || [];

            const hasEnoughUserReviews = reviews.length >= MAX_PRODUCT_REVIEWS_WITH_INITIAL_REVIEWS;

            const combinedReviews = [...reviews, ...hardcodedInitialReviews]
                .filter(({ title, rating }) => title && rating >= MIN_RATING)
                .slice(0, hasEnoughUserReviews ? MAX_PRODUCT_REVIEWS : MAX_PRODUCT_REVIEWS_WITH_INITIAL_REVIEWS);

            setNormalizedReviews(combinedReviews);
        });
    }, [inView, sku, initialReviews, normalizedReviews?.length, get]);

    useEffect(() => {
        if (!normalizedReviews) {
            return;
        }

        if (normalizedReviews.length < VISIBLE_PRODUCT_REVIEWS && initialReviews) {
            setProductReviews([...normalizedReviews, ...initialReviews].slice(0, VISIBLE_PRODUCT_REVIEWS));
            return;
        }

        setProductReviews(normalizedReviews.slice(0, VISIBLE_PRODUCT_REVIEWS));
    }, [initialReviews, normalizedReviews]);

    const showAllProductReviews = () => {
        if (normalizedReviews?.length) {
            setProductReviews(normalizedReviews);
        }
    };

    return { productReviews, showAllProductReviews, showLoadMoreButton };
};

export { useProductReviews };
